import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";

// ── Icons (inline SVG so zero deps) ──────────────────────────────────────────
const Icon = {
  success: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="10" fill="#16a34a" />
      <path d="M5.5 10.5l3 3 6-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="10" fill="#dc2626" />
      <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="10" fill="#d97706" />
      <path d="M10 6v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      <circle cx="10" cy="14" r="1" fill="#fff" />
    </svg>
  ),
  info: (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="10" fill="#1565C0" />
      <path d="M10 9v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      <circle cx="10" cy="6.5" r="1" fill="#fff" />
    </svg>
  ),
};

// ── Per-type colour tokens ────────────────────────────────────────────────────
const THEME = {
  success: { bg: "#f0fdf4", border: "#bbf7d0", titleColor: "#14532d", msgColor: "#166534" },
  error:   { bg: "#fef2f2", border: "#fecaca", titleColor: "#7f1d1d", msgColor: "#991b1b" },
  warning: { bg: "#fffbeb", border: "#fde68a", titleColor: "#78350f", msgColor: "#92400e" },
  info:    { bg: "#eff6ff", border: "#bfdbfe", titleColor: "#1e3a5f", msgColor: "#1d4ed8" },
};

// ── Single Toast item ─────────────────────────────────────────────────────────
function ToastItem({ id, type, title, message, onRemove }) {
  const theme = THEME[type] || THEME.info;
  const [visible, setVisible] = React.useState(false);
  const [leaving, setLeaving] = React.useState(false);

  // Entrance animation on mount
  React.useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Auto-dismiss after 3.5 s
  React.useEffect(() => {
    const timer = setTimeout(() => dismiss(), 3500);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setLeaving(true);
    setTimeout(() => onRemove(id), 320);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        background: theme.bg,
        border: `1.5px solid ${theme.border}`,
        borderRadius: 12,
        padding: "12px 14px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
        width: 320,
        maxWidth: "calc(100vw - 40px)",
        position: "relative",
        cursor: "pointer",
        userSelect: "none",
        // slide-in / slide-out
        opacity: visible && !leaving ? 1 : 0,
        transform: visible && !leaving ? "translateX(0)" : "translateX(30px)",
        transition: "opacity 0.28s ease, transform 0.28s ease",
      }}
      onClick={dismiss}
      title="Click to dismiss"
    >
      {/* Icon */}
      <div style={{ flexShrink: 0, marginTop: 1 }}>{Icon[type]}</div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && (
          <div style={{
            fontSize: 13.5,
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: message ? 2 : 0,
            lineHeight: 1.3,
          }}>
            {title}
          </div>
        )}
        {message && (
          <div style={{
            fontSize: 12.5,
            color: theme.msgColor,
            lineHeight: 1.45,
            wordBreak: "break-word",
          }}>
            {message}
          </div>
        )}
      </div>

      {/* Close × */}
      <button
        onClick={(e) => { e.stopPropagation(); dismiss(); }}
        style={{
          background: "none",
          border: "none",
          color: theme.titleColor,
          opacity: 0.5,
          fontSize: 16,
          lineHeight: 1,
          cursor: "pointer",
          padding: 0,
          flexShrink: 0,
          marginTop: -1,
        }}
        aria-label="close"
      >
        ×
      </button>
    </div>
  );
}

// ── Context ───────────────────────────────────────────────────────────────────
const ToastContext = createContext(null);

// ── Provider (mount once near the top of your tree) ──────────────────────────
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const lastToastTimeRef = useRef(0);

  const add = useCallback((type, titleOrMsg, message) => {
    const now = Date.now();
    if (now - lastToastTimeRef.current < 3000) return;
    lastToastTimeRef.current = now;
    const title   = message ? titleOrMsg : undefined;
    const msg     = message ? message    : titleOrMsg;
    const id      = ++counterRef.current;
    setToasts((prev) => [...prev, { id, type, title, message: msg }]);
  }, []);



  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api = {
    success: (t, m) => add("success", t, m),
    error:   (t, m) => add("error",   t, m),
    warning: (t, m) => add("warning", t, m),
    info:    (t, m) => add("info",    t, m),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}

      {/* Portal-like fixed container — bottom-right */}
      <div
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <div key={t.id} style={{ pointerEvents: "auto" }}>
            <ToastItem
              id={t.id}
              type={t.type}
              title={t.title}
              message={t.message}
              onRemove={remove}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}